import { Link } from "react-router-dom";
import { Shield, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const insurers = [
  { name: "Porto Seguro", logo: "/logos/porto-seguro.png", desc: "Líder de mercado em seguro auto no Brasil. Reconhecida pela excelência no atendimento, assistência 24h completa e app de serviços." },
  { name: "Allianz", logo: "/logos/allianz.png", desc: "Uma das maiores seguradoras do mundo, presente em mais de 70 países. Oferece seguros auto, empresarial, vida e residencial com coberturas diferenciadas." },
  { name: "Zurich", logo: "/logos/zurich.png", desc: "Grupo suíço com presença global. Oferece seguros para pessoas físicas e jurídicas com coberturas abrangentes e assistência premium." },
  { name: "SulAmérica", logo: "/logos/sulamerica.png", desc: "Uma das maiores seguradoras independentes do Brasil, com mais de 125 anos de história. Destaque em saúde, vida, previdência e investimentos." },
  { name: "Bradesco Seguros", logo: "/logos/bradesco.png", desc: "Uma das maiores seguradoras do Brasil, com ampla rede de atendimento e diversas opções de produtos para pessoas físicas e jurídicas." },
  { name: "Tokio Marine", logo: "/logos/tokio-marine.png", desc: "Presente em mais de 40 países, é reconhecida no Brasil pelo excelente atendimento ao segurado e boas condições de renovação." },
  { name: "HDI", logo: "/logos/hdi.png", desc: "Seguradora alemã com forte presença no Brasil. Conhecida pelo ótimo custo-benefício e ampla rede de oficinas referenciadas." },
  { name: "Itaú Seguros", logo: "/logos/itau.png", desc: "Braço segurador do Itaú Unibanco. Oferece seguros de auto, vida, residencial e empresarial com a solidez de um dos maiores bancos da América Latina." },
  { name: "Mapfre", logo: "/logos/mapfre.png", desc: "Multinacional espanhola presente em mais de 40 países. No Brasil, oferece seguros auto, vida, residencial e empresarial com forte atuação no agronegócio." },
  { name: "Generali", logo: "/logos/generali.png", desc: "Grupo italiano com quase 200 anos de história. Oferece seguros de vida, auto e empresarial com coberturas internacionais diferenciadas." },
  { name: "Mitsui Sumitomo", logo: "/logos/mitsui.png", desc: "Seguradora japonesa com atuação global. No Brasil, oferece seguros com coberturas robustas para automóveis, empresas e residências." },
  { name: "Yelum", logo: "/logos/yelum.png", desc: "Seguradora com foco em inovação e atendimento personalizado. Oferece soluções flexíveis em seguros auto, residencial e empresarial." },
  { name: "MAG", logo: "/logos/mag.png", desc: "Com mais de 190 anos de história, a MAG é líder em seguros de vida individual no Brasil. Oferece seguros de vida, previdência privada e produtos financeiros." },
  { name: "Azul Seguros", logo: "/logos/azul.png", desc: "Pertencente ao grupo Porto Seguro, oferece seguros com preços competitivos e atendimento de qualidade. Especializada em seguro auto acessível." },
  { name: "Suhai", logo: "/logos/suhai.png", desc: "Especializada em seguros acessíveis, atendendo um público que busca proteção com economia. Focada em seguro auto popular." },
];

export default function Seguradoras() {
  return (
    <div data-testid="seguradoras-page">
      {/* Hero */}
      <section className="pt-20 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <Shield className="h-4 w-4 text-gold" />
            <span className="text-sm text-white/80 font-medium">Seguradoras Parceiras</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white leading-tight mb-4">
            Nossas <span className="text-gold">seguradoras parceiras</span>
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl mx-auto">
            Trabalhamos com as melhores seguradoras do Brasil para comparar opções e encontrar o seguro ideal para cada cliente.
          </p>
        </div>
      </section>

      {/* Advantage */}
      <section className="py-12 bg-gold/5 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-base text-slate-700 font-medium">
            A vantagem de contar com uma corretora: <span className="text-brand font-semibold">comparamos preços e coberturas</span> de múltiplas seguradoras para encontrar a melhor opção para o seu perfil.
          </p>
        </div>
      </section>

      {/* Insurers Grid */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            {insurers.map((ins, i) => (
              <div key={i} className="flex gap-5 bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg transition-shadow" data-testid={`insurer-card-${i}`}>
                <div className="w-16 h-16 rounded-xl bg-slate-50 border flex items-center justify-center shrink-0 overflow-hidden">
                  <img src={ins.logo} alt={`Logo ${ins.name} seguradora parceira`} className="w-10 h-10 object-contain" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-navy text-base mb-1.5">{ins.name}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{ins.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-50 border-t">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-navy mb-4">Quer saber qual seguradora é melhor para você?</h2>
          <p className="text-base text-slate-500 mb-6">Solicite uma cotação gratuita e nossa equipe fará a comparação.</p>
          <Link to="/cotacao">
            <Button className="btn-gold h-12 px-8 text-base" data-testid="seguradoras-cta">
              Solicitar Cotação Gratuita <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
